import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router";
import {
  Activity,
  BookOpen,
  FolderTree,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";
import { signOut } from "../api/auth";
import { useMe } from "./MeProvider";

type Need = "dashboard" | "content";

/**
 * The dashboard holds every number, so it is the one item a product manager
 * (no content access) and a worker both see — each gets the sections their
 * role allows.
 */
const NAV: { to: string; label: string; icon: LucideIcon; need: Need }[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, need: "dashboard" },
  { to: "/books", label: "Books", icon: BookOpen, need: "content" },
  { to: "/questions", label: "Questions", icon: ListChecks, need: "content" },
  { to: "/topics", label: "Topics", icon: FolderTree, need: "content" },
  { to: "/activity", label: "Activity", icon: Activity, need: "content" },
];

const COLLAPSED_KEY = "prepai.worker.sidebar";

export function Layout() {
  const navigate = useNavigate();
  const { me } = useMe();
  // Until /me answers, show the content links (what nearly everyone is).
  const nav = NAV.filter((item) => item.need === "dashboard" || !me || me.can.content);
  const badge = me?.can.admin ? "ADMIN" : me?.role === "product_manager" ? "PRODUCT" : "WORKER";
  const location = useLocation();

  // Desktop: collapsed rail vs expanded
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Mobile: drawer open/closed
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Persist collapsed state
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      /* */
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate("/login");
  };

  // ── Sidebar content (shared between desktop sidebar and mobile drawer) ──

  const sidebarContent = (isMobile: boolean) => {
    const expanded = isMobile || !collapsed;

    return (
      <>
        {/* Logo */}
        <div
          className={`flex h-16 items-center gap-2.5 px-4 ${!expanded ? "justify-center px-0" : ""}`}
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent font-[family-name:var(--font-display)] text-[15px] font-bold text-bg">
            P
          </span>
          {expanded ? (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="font-[family-name:var(--font-display)] text-[16px] font-bold">
                Prep<span className="text-accent">AI</span>
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                {badge}
              </span>
            </div>
          ) : null}
        </div>

        {/* Nav links */}
        <nav className="flex flex-1 flex-col gap-1 px-3 pt-2">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              title={!expanded ? item.label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors ${
                  !expanded ? "justify-center" : ""
                } ${
                  isActive
                    ? "bg-surface-high text-text before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-accent"
                    : "text-text-muted hover:bg-surface-high/60 hover:text-text"
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.8} aria-hidden="true" className="shrink-0" />
              {expanded ? item.label : null}

              {/* Tooltip for collapsed desktop */}
              {!expanded && !isMobile ? (
                <span className="pointer-events-none absolute left-full ml-2 hidden whitespace-nowrap rounded-lg bg-surface-high px-2.5 py-1.5 text-[12px] font-medium text-text shadow-lg group-hover:block">
                  {item.label}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="border-t border-border p-3">
          {/* Collapse toggle — desktop only */}
          {!isMobile ? (
            <button
              type="button"
              onClick={toggleCollapsed}
              className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-text-muted transition-colors hover:bg-surface-high hover:text-text ${
                !expanded ? "justify-center" : ""
              }`}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <PanelLeftOpen size={18} strokeWidth={1.8} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={18} strokeWidth={1.8} aria-hidden="true" />
              )}
              {expanded ? "Collapse" : null}
            </button>
          ) : null}

          <button
            type="button"
            onClick={handleSignOut}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-text-muted transition-colors hover:bg-surface-high hover:text-text ${
              !expanded ? "justify-center" : ""
            }`}
            title={!expanded ? "Sign out" : undefined}
          >
            <LogOut size={18} strokeWidth={1.8} aria-hidden="true" />
            {expanded ? "Sign out" : null}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="flex min-h-screen">
      {/* ── Mobile top bar ── */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-surface px-4 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-high hover:text-text"
          aria-label="Open menu"
        >
          <Menu size={20} strokeWidth={1.8} aria-hidden="true" />
        </button>
        <span className="font-[family-name:var(--font-display)] text-[16px] font-bold">
          Prep<span className="text-accent">AI</span>
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
            {badge}
          </span>
        </span>
        <button
          type="button"
          onClick={handleSignOut}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-high hover:text-text"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={18} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </header>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/60"
          />
          <aside className="relative flex h-full w-64 flex-col overflow-y-auto border-r border-border bg-surface shadow-2xl">
            {sidebarContent(true)}
          </aside>
        </div>
      ) : null}

      {/* ── Desktop sidebar ── */}
      <aside
        // Pinned to the viewport: the page scrolls, the sidebar doesn't stretch
        // with it, and Collapse / Sign out stay in reach on long tables.
        className={`sticky top-0 hidden h-screen shrink-0 flex-col overflow-y-auto border-r border-border bg-surface transition-[width] duration-200 lg:flex ${
          collapsed ? "w-16" : "w-56"
        }`}
      >
        {sidebarContent(false)}
      </aside>

      {/* ── Main content ── */}
      <main className="min-w-0 flex-1 bg-bg pt-14 lg:pt-0">
        <div className="mx-auto w-full max-w-7xl p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
